/**
 * Mobile Styles Utility
 * 
 * Centralized mobile styling helpers for consistent mobile UX across the application.
 * Use these utilities to ensure consistent padding, spacing, and responsive behavior.
 * 
 * Usage:
 *   import { getMobilePadding, getMobileClasses, mobileTableToCards } from '../utils/mobileStyles';
 *   
 *   // In component:
 *   const isMobile = useIsMobile();
 *   <div className={getMobilePadding(isMobile)}>...</div>
 */

/**
 * Get responsive padding classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind padding classes
 */
export function getMobilePadding(isMobile) {
  return isMobile ? 'p-4' : 'p-6';
}

/**
 * Get responsive padding with custom values
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @param {string} mobilePadding - Padding for mobile (default: 'p-4')
 * @param {string} desktopPadding - Padding for desktop (default: 'p-6')
 * @returns {string} Tailwind padding classes
 */
export function getResponsivePadding(isMobile, mobilePadding = 'p-4', desktopPadding = 'p-6') {
  return isMobile ? mobilePadding : desktopPadding;
}

/**
 * Get responsive horizontal padding classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind padding classes
 */
export function getMobileHorizontalPadding(isMobile) {
  return isMobile ? 'px-4' : 'px-6';
}

/**
 * Get responsive vertical padding classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind padding classes
 */
export function getMobileVerticalPadding(isMobile) {
  return isMobile ? 'py-4' : 'py-6';
}

/**
 * Get responsive gap classes for flex/grid containers
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind gap classes
 */
export function getMobileGap(isMobile) {
  return isMobile ? 'gap-3' : 'gap-4';
}

/**
 * Get responsive text size classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @param {string} type - Type of text: 'heading', 'subheading', 'body', 'small'
 * @returns {string} Tailwind text size classes
 */
export function getMobileTextSize(isMobile, type = 'body') {
  const sizes = {
    heading: isMobile ? 'text-xl' : 'text-2xl',
    subheading: isMobile ? 'text-lg' : 'text-xl',
    body: isMobile ? 'text-sm' : 'text-base',
    small: isMobile ? 'text-xs' : 'text-sm',
  };
  return sizes[type] || sizes.body;
}

/**
 * Get responsive button width classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind width classes
 */
export function getMobileButtonWidth(isMobile) {
  return isMobile ? 'w-full' : 'w-auto';
}

/**
 * Get responsive grid columns
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @param {number} desktopCols - Number of columns on desktop (default: 2)
 * @returns {string} Tailwind grid classes
 */
export function getMobileGridCols(isMobile, desktopCols = 2) {
  const colsMap = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };
  return isMobile ? 'grid-cols-1' : (colsMap[desktopCols] || 'grid-cols-2');
}

/**
 * Get responsive flex direction
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind flex direction classes
 */
export function getMobileFlexDirection(isMobile) {
  return isMobile ? 'flex-col' : 'flex-row';
}

/**
 * Get classes to hide element on mobile
 * @returns {string} Tailwind classes to hide on mobile
 */
export function hideOnMobile() {
  return 'hidden sm:block';
}

/**
 * Get classes to show element only on mobile
 * @returns {string} Tailwind classes to show only on mobile
 */
export function showOnlyOnMobile() {
  return 'block sm:hidden';
}

/**
 * Get responsive container max-width
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind max-width classes
 */
export function getMobileMaxWidth(isMobile) {
  return isMobile ? 'max-w-full' : 'max-w-3xl';
}

/**
 * Get MUI sx props for responsive padding
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {object} MUI sx padding object
 */
export function getMuiPadding(isMobile) {
  return { p: isMobile ? 2 : 3 };
}

/**
 * Get MUI sx props for responsive padding using breakpoints
 * @returns {object} MUI sx padding object with breakpoints
 */
export function getMuiResponsivePadding() {
  return { p: { xs: 2, sm: 3 } };
}

/**
 * Get MUI sx props for responsive margin
 * @returns {object} MUI sx margin object with breakpoints
 */
export function getMuiResponsiveMargin() {
  return { m: { xs: 1, sm: 2 } };
}

/**
 * Get MUI Dialog props for mobile fullscreen
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {object} MUI Dialog props
 */
export function getMobileDialogProps(isMobile) {
  return {
    fullScreen: isMobile,
    maxWidth: isMobile ? false : 'md',
    fullWidth: true,
    PaperProps: {
      sx: {
        maxHeight: isMobile ? '100%' : '90vh',
        marginTop: isMobile ? 0 : '5vh',
      },
    },
  };
}

/**
 * Get MUI Button props for mobile
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {object} MUI Button props
 */
export function getMobileButtonProps(isMobile) {
  return {
    fullWidth: isMobile,
    size: isMobile ? 'large' : 'medium',
  };
}

/**
 * Get bottom padding for pages with bottom navigation
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind padding classes
 */
export function getBottomNavPadding(isMobile) {
  return isMobile ? 'pb-24' : 'pb-6';
}

/**
 * Combine multiple class strings, filtering out falsy values
 * @param  {...string} classes - Class strings to combine
 * @returns {string} Combined class string
 */
export function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Mobile-first responsive classes builder
 * @param {object} options - Options object
 * @param {string} options.base - Base classes (always applied)
 * @param {string} options.mobile - Mobile-only classes
 * @param {string} options.desktop - Desktop-only classes (sm: and up)
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Combined class string
 */
export function responsiveClasses({ base = '', mobile = '', desktop = '' }, isMobile) {
  return cx(base, isMobile ? mobile : desktop);
}

/**
 * Standard page container classes for mobile optimization
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind classes for page container
 */
export function getPageContainerClasses(isMobile) {
  return cx(
    'min-h-full',
    getMobilePadding(isMobile),
    getBottomNavPadding(isMobile)
  );
}

/**
 * Standard card classes for mobile optimization
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind classes for cards
 */
export function getCardClasses(isMobile) {
  return cx(
    'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700',
    isMobile ? 'p-4' : 'p-6'
  );
}

/**
 * Standard section header classes
 * @param {boolean} isMobile - Whether the viewport is mobile
 * @returns {string} Tailwind classes for section headers
 */
export function getSectionHeaderClasses(isMobile) {
  return cx(
    'font-semibold text-gray-900 dark:text-white',
    getMobileTextSize(isMobile, 'subheading')
  );
}

/**
 * Get touch-friendly minimum height for interactive elements
 * @returns {string} Tailwind min-height class (44px for touch targets)
 */
export function getTouchTargetHeight() {
  return 'min-h-[44px]';
}

/**
 * Get safe area inset padding for notched devices
 * @returns {object} Style object with safe area insets
 */
export function getSafeAreaInsets() {
  return {
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    paddingLeft: 'env(safe-area-inset-left, 0px)',
    paddingRight: 'env(safe-area-inset-right, 0px)',
  };
}

// Export all utilities as a default object for convenience
export default {
  getMobilePadding,
  getResponsivePadding,
  getMobileHorizontalPadding,
  getMobileVerticalPadding,
  getMobileGap,
  getMobileTextSize,
  getMobileButtonWidth,
  getMobileGridCols,
  getMobileFlexDirection,
  hideOnMobile,
  showOnlyOnMobile,
  getMobileMaxWidth,
  getMuiPadding,
  getMuiResponsivePadding,
  getMuiResponsiveMargin,
  getMobileDialogProps,
  getMobileButtonProps,
  getBottomNavPadding,
  cx,
  responsiveClasses,
  getPageContainerClasses,
  getCardClasses,
  getSectionHeaderClasses,
  getTouchTargetHeight,
  getSafeAreaInsets,
};
